import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

// Material UI
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

// Components
import PartTechnicianRequirementForm from './PartTechnicianRequirementForm';

// Services
import * as partTechnicianRequirementService from '../services/partTechnicianRequirementService';

const PartTechnicianRequirementList = ({ partId, requirement, onRequirementChange }) => {
  const [openForm, setOpenForm] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [formMode, setFormMode] = useState('add'); // 'add' or 'edit'

  const handleAddClick = () => {
    setSelectedRequirement(null);
    setFormMode('add');
    setOpenForm(true);
  };

  const handleEditClick = (req) => {
    setSelectedRequirement(req);
    setFormMode('edit');
    setOpenForm(true);
  };

  const handleDeleteClick = (req) => {
    setSelectedRequirement(req);
    setOpenDeleteDialog(true);
  };

  const handleFormClose = () => {
    setOpenForm(false);
    setSelectedRequirement(null);
  };

  const handleDeleteConfirm = async () => {
    try {
      await partTechnicianRequirementService.deletePartTechnicianRequirement(selectedRequirement.id);
      
      toast.success('Technician requirement deleted successfully');
      
      // Notify parent component
      if (onRequirementChange) {
        onRequirementChange(null);
      }
      
      setOpenDeleteDialog(false);
      setSelectedRequirement(null);
    } catch (err) {
      console.error('Error deleting technician requirement:', err);
      toast.error(err.message || 'Failed to delete technician requirement');
    }
  };

  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
    setSelectedRequirement(null);
  };

  const handleRequirementChange = (updatedRequirement) => {
    if (onRequirementChange) {
      onRequirementChange(updatedRequirement);
    }
  };

  return (
    <Card sx={{ mt: 3 }}>
      <CardHeader
        title="Technician Requirements"
        subheader="Set the quantity required per technician for this part"
        action={
          !requirement ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddClick}
              size="small"
            >
              Add Requirement
            </Button>
          ) : null
        }
      />
      <CardContent>
        {requirement ? (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Quantity Per Technician</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>{requirement.quantity_per_technician}</TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditClick(requirement)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(requirement)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No technician requirement set for this part.
          </Typography>
        )}
      </CardContent>

      {/* Technician Requirement Form Dialog */}
      <PartTechnicianRequirementForm
        partId={partId}
        requirement={selectedRequirement}
        onRequirementChange={handleRequirementChange}
        open={openForm}
        onClose={handleFormClose}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Delete Technician Requirement</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this technician requirement? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default PartTechnicianRequirementList;
